import axios from 'axios'
import store from '@/store'
import router from '@/router'

const api = axios.create({
  baseURL: '/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// 防止多個請求同時嘗試 refresh token（只執行一次）
let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    // 若是 401 且不是 refresh/login 請求本身（避免無限循環）
    const isAuthEndpoint =
      originalRequest.url?.includes('/auth/refresh') ||
      originalRequest.url?.includes('/auth/login') ||
      originalRequest.url?.includes('/auth/google')

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        // 已有 refresh 進行中，排隊等待
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then((token) => {
            originalRequest.headers['Authorization'] = `Bearer ${token}`
            return api(originalRequest)
          })
          .catch((err) => Promise.reject(err))
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const refreshed = await store.dispatch('auth/refreshToken')
        if (refreshed) {
          const newToken = localStorage.getItem('access_token')
          processQueue(null, newToken)
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`
          return api(originalRequest)
        } else {
          processQueue(new Error('Session expired'))
          // Session 已過期，導向登入頁並帶提示（避免已在登入頁時重複導航）
          if (router.currentRoute?.name !== 'AdminLogin') {
            router.push({ name: 'AdminLogin', query: { reason: 'session_expired' } })
          }
          return Promise.reject(error)
        }
      } catch (refreshError) {
        processQueue(refreshError)
        if (router.currentRoute?.name !== 'AdminLogin') {
          router.push({ name: 'AdminLogin', query: { reason: 'session_expired' } })
        }
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  }
)

export default api
